/* eslint-disable consistent-return */
import concat from "lodash-es/concat"
import eq from "lodash-es/eq"
import every from "lodash-es/every"
import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"
import map from "lodash-es/map"
import orderBy from "lodash-es/orderBy"
import { DateTime } from "luxon"

import type { TTFunction } from "$/i18n/i18n"

import type { ISelectOption, TAvatarListUser } from "@/3514/components"
import type { ISessionExtraPartyModel, ISessionParticipantModel } from "@/3514/types"

import type { ICoachDashboardParticipantsQueryParticipantDto as IUserParticipantDto } from "./coach.dashboard.api.types"
import {
    ECoachDashboardTableColumnName as EColName,
    ECoachDashboardPeriodFilter as EFilter,
    type TCoachDashboardPaginationRangeGetterFunction as TRangeGetter,
    type TCoachDashboardPaginationRangeGetterFunctionParams as TRangeGetterParams,
    type TCoachDashboardTableSortType as TTableSortType,
    type TCoachDashboardTableSorter as TTableSorter
} from "./coach.dashboard.ui.types"

function getAttendanceEntityRowItemDate(date: string): string {
    return DateTime.fromISO(date).toFormat("ccc, LLL dd")
}

function getAttendanceEntityRowItemTime(date: string): string {
    return DateTime.fromISO(date).toFormat("hh:mma").toLowerCase()
}

function getPanelTimezone(): string {
    return DateTime.local().zoneName
}

function getTableRowItemDate(
    date: string,
    config: { withYear?: boolean; withShortYear?: boolean } = { withYear: false, withShortYear: false }
): string {
    if (!isString(date)) return "Invalid date"

    return config.withYear
        ? DateTime.fromISO(date).toFormat(config.withShortYear ? "ccc, LLL dd, yy" : "ccc, LLL dd, yyyy")
        : DateTime.fromISO(date).toFormat("ccc, LLL dd")
}

function getTableRowItemTime(date: string): string {
    if (!isString(date)) return "Invalid time"

    return DateTime.fromISO(date).toFormat("hh:mma").toLowerCase()
}

function getTableRowItemYearDate(date: string): string {
    if (!isString(date)) return "Invalid time"

    return DateTime.fromISO(date).toFormat("LLL d, yyyy")
}

function getTableRowAvatarsList(
    models: ISessionParticipantModel[],
    extraParties: ISessionExtraPartyModel[]
): TAvatarListUser[] {
    const initialList: TAvatarListUser[] = isEmpty(models)
        ? []
        : map<ISessionParticipantModel, TAvatarListUser>(
              models,
              ({ id, firstName, lastName, photo, isGuest }: ISessionParticipantModel): TAvatarListUser => ({
                  id,
                  firstName,
                  lastName,
                  photo,
                  isGuest: Boolean(isGuest),
                  isExtraParty: false
              })
          )

    if (isEmpty(extraParties)) return initialList

    return concat<TAvatarListUser>(
        ...initialList,
        map<ISessionExtraPartyModel, TAvatarListUser>(
            extraParties,
            (e: ISessionExtraPartyModel): TAvatarListUser => ({
                id: e.id,
                firstName: e.firstName,
                lastName: undefined,
                photo: undefined,
                isGuest: false,
                isExtraParty: true
            })
        )
    )
}

function getSelectOptions(params: { t: TTFunction }): ISelectOption[] {
    return [
        {
            id: EFilter.UpcomingSessions,
            value: EFilter.UpcomingSessions,
            label: params.t("coachSide.dashboard.dataPanel.header.select.label.upcoming")
        },
        {
            id: EFilter.PastSessions,
            value: EFilter.UpcomingSessions,
            label: params.t("coachSide.dashboard.dataPanel.header.select.label.past")
        }
    ]
}

function _getPaginationArr(start: number, end: number): number[] {
    return Array.from({ length: end - start + 1 }, (_: never, idx: number): number => idx + start)
}

function getTablePaginationRange({
    totalCount,
    pageSize = 10,
    siblingCount = 1,
    currentPage,
    filler
}: TRangeGetterParams): ReturnType<TRangeGetter> {
    const totalPageCount: number = Math.ceil(totalCount / pageSize)

    const totalPageNumbers: number = siblingCount + 5

    if (totalPageNumbers >= totalPageCount) return _getPaginationArr(1, totalPageCount)

    const leftSiblingIndex: number = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex: number = Math.min(currentPage + siblingCount, totalPageCount)

    const shouldShowLeftDots: boolean = leftSiblingIndex > 2
    const shouldShowRightDots: boolean = rightSiblingIndex < totalPageCount - 2

    const firstPageIndex = 1
    const lastPageIndex: number = totalPageCount

    if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount: number = 3 + 2 * siblingCount
        const leftRange: number[] = _getPaginationArr(1, leftItemCount)

        return [...leftRange, filler, totalPageCount]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
        const rightItemCount: number = 3 + 2 * siblingCount
        const rightRange: number[] = _getPaginationArr(totalPageCount - rightItemCount + 1, totalPageCount)

        return [firstPageIndex, filler, ...rightRange]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
        const middleRange: number[] = _getPaginationArr(leftSiblingIndex, rightSiblingIndex)

        return [firstPageIndex, filler, ...middleRange, filler, lastPageIndex]
    }
}

function getParticipantsTableSortHandlers(): {
    [C in EColName.Participants_Name | EColName.Participants_Sessions]: {
        [sort in TTableSortType]: TTableSorter
    }
} {
    function _getFirstTimestamp(p: IUserParticipantDto): number {
        const iso: string = p?.upcoming_sessions?.[0]?.session_time
        const ts: number = iso ? DateTime.fromISO(iso).toMillis() : NaN
        return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY
    }

    function _getFallback(p: IUserParticipantDto): number {
        return p.id
    }

    return {
        [EColName.Participants_Name]: {
            asc: (arr: IUserParticipantDto[]): IUserParticipantDto[] =>
                orderBy<IUserParticipantDto>(
                    arr,
                    [(p: IUserParticipantDto): string => p.first_name, _getFallback],
                    ["asc", "asc"]
                ),
            desc: (arr: IUserParticipantDto[]): IUserParticipantDto[] =>
                orderBy<IUserParticipantDto>(
                    arr,
                    [(p: IUserParticipantDto): string => p.first_name, _getFallback],
                    ["desc", "asc"]
                )
        },

        [EColName.Participants_Sessions]: {
            asc: (arr: IUserParticipantDto[]): IUserParticipantDto[] =>
                orderBy<IUserParticipantDto>(arr, [_getFirstTimestamp, _getFallback], ["asc", "asc"]),
            desc: (arr: IUserParticipantDto[]): IUserParticipantDto[] =>
                orderBy<IUserParticipantDto>(arr, [_getFirstTimestamp, _getFallback], ["desc", "asc"])
        }
    }
}

function getIsTableSameSortOrder(existing: IUserParticipantDto[], sorted: IUserParticipantDto[]): boolean {
    return (
        eq(existing.length, sorted.length) &&
        every<IUserParticipantDto>(existing, (it: IUserParticipantDto, i: number): boolean => eq(it, sorted[i]))
    )
}

export default {
    getAttendanceEntityRowItemDate,
    getAttendanceEntityRowItemTime,
    getPanelTimezone,
    getSelectOptions,
    getTableRowItemDate,
    getTableRowItemTime,
    getTableRowItemYearDate,
    getTableRowAvatarsList,
    getTablePaginationRange,
    getParticipantsTableSortHandlers,
    getIsTableSameSortOrder
}
